import { Link } from 'react-router-dom';
import { Download, Loader2, Percent } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { formatCurrency, formatDate } from '@/lib/utils';
import { downloadXlsx, dateStamp } from '@/lib/download-xlsx';
import { useSessionState } from '@/hooks/use-session-state';

type App = 'TRAVEL' | 'TRADING';

const KIND_LABEL: Record<string, string> = {
  EXPENSE: 'Masraf / Expense',
  SUPPLIER_INVOICE: 'Tedarikçi faturası / Supplier invoice',
  GOODS_RECEIPT: 'Mal kabul / Goods receipt',
};

function documentHref(app: App, kind: string, id: string): string | null {
  if (app === 'TRAVEL' && kind === 'EXPENSE') return `/travel/expenses/${id}`;
  if (app === 'TRADING' && kind === 'SUPPLIER_INVOICE') return `/trading/finance/supplier-invoices/${id}`;
  if (app === 'TRADING' && kind === 'GOODS_RECEIPT') return `/trading/purchasing/goods-receipts/${id}`;
  return null;
}

/**
 * Stopaj / tevkifat beyanname eki — every withholding the company retained in
 * the period, grouped by type × rate × currency with the document trail, and
 * an XLSX export for the declaration.
 */
export function WithholdingReport({ app }: { app: App }) {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const key = app.toLowerCase();
  const [startDate, setStartDate] = useSessionState(`${key}:accounting-reports:withholding:startDate`, startOfMonth.toISOString().split('T')[0] ?? '');
  const [endDate, setEndDate] = useSessionState(`${key}:accounting-reports:withholding:endDate`, today.toISOString().split('T')[0] ?? '');
  const input = { startDate: new Date(startDate || startOfMonth), endDate: new Date(endDate || today) };

  const travel = trpc.financialReports.getWithholdingSummary.useQuery(input, { enabled: app === 'TRAVEL' });
  const trading = trpc.tradingFinancialReports.getWithholdingSummary.useQuery(input, { enabled: app === 'TRADING' });
  const query = app === 'TRAVEL' ? travel : trading;
  const data = query.data;

  const exportRows = () => {
    if (!data) return;
    downloadXlsx(
      data.rows.map((r) => ({
        Tür: KIND_LABEL[r.kind] ?? r.kind,
        Belge: r.documentNumber,
        Tarih: formatDate(r.date),
        Cari: r.party,
        'Stopaj türü': r.type ?? '',
        'Oran %': r.rate ?? '',
        'Para birimi': r.currency,
        Matrah: r.base,
        'Kesilen tutar': r.withheld,
      })),
      'Stopaj',
      `stopaj-${startDate}-${endDate}-${dateStamp()}.xlsx`
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-card flex flex-wrap items-end gap-4 rounded-lg border p-4">
        <div className="space-y-2">
          <Label>Başlangıç / From</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-48" />
        </div>
        <div className="space-y-2">
          <Label>Bitiş / To</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-48" />
        </div>
        <Button variant="outline" className="h-10" disabled={!data || data.rows.length === 0} onClick={exportRows}>
          <Download className="mr-2 h-4 w-4" />
          Excel
        </Button>
      </div>

      {query.isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : query.error ? (
        <div className="border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">{query.error.message}</div>
      ) : data ? (
        <>
          <div className="grid gap-3 md:grid-cols-3">
            {data.totals.length === 0 ? (
              <div className="bg-card rounded-lg border p-4 text-sm text-muted-foreground">Bu dönemde stopaj/tevkifat yok. / No withholding in this period.</div>
            ) : (
              data.totals.map((t) => (
                <div key={t.currency} className="bg-card rounded-lg border p-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Percent className="h-4 w-4" />
                    {t.currency} · {t.count} belge
                  </div>
                  <div className="mt-1 text-2xl font-bold tabular-nums">{formatCurrency(t.withheld, t.currency)}</div>
                  <div className="text-xs text-muted-foreground">Matrah / Base: {formatCurrency(t.base, t.currency)}</div>
                </div>
              ))
            )}
          </div>

          {data.groups.length > 0 && (
            <div className="bg-card overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 font-medium">Stopaj türü / Type</th>
                    <th className="px-4 py-2 text-right font-medium">Oran / Rate</th>
                    <th className="px-4 py-2 font-medium">Para birimi</th>
                    <th className="px-4 py-2 text-right font-medium">Belge</th>
                    <th className="px-4 py-2 text-right font-medium">Matrah / Base</th>
                    <th className="px-4 py-2 text-right font-medium">Kesilen / Withheld</th>
                  </tr>
                </thead>
                <tbody>
                  {data.groups.map((g) => (
                    <tr key={`${g.type}|${g.rate}|${g.currency}`} className="border-b last:border-b-0">
                      <td className="px-4 py-2">{g.type ?? '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{g.rate == null ? '—' : `%${g.rate}`}</td>
                      <td className="px-4 py-2 font-mono text-xs">{g.currency}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{g.count}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatCurrency(g.base, g.currency)}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">{formatCurrency(g.withheld, g.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.rows.length > 0 && (
            <div className="bg-card overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 font-medium">Belge / Document</th>
                    <th className="px-4 py-2 font-medium">Tarih</th>
                    <th className="px-4 py-2 font-medium">Cari / Party</th>
                    <th className="px-4 py-2 font-medium">Tür</th>
                    <th className="px-4 py-2 text-right font-medium">Oran</th>
                    <th className="px-4 py-2 text-right font-medium">Matrah</th>
                    <th className="px-4 py-2 text-right font-medium">Kesilen</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => {
                    const href = documentHref(app, r.kind, r.id);
                    return (
                      <tr key={`${r.kind}:${r.id}`} className="border-b last:border-b-0">
                        <td className="px-4 py-2 font-mono text-xs">
                          {href ? (
                            <Link to={href} className="hover:underline">
                              {r.documentNumber}
                            </Link>
                          ) : (
                            r.documentNumber
                          )}
                          <span className="ml-2 text-[11px] text-muted-foreground">{KIND_LABEL[r.kind] ?? r.kind}</span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">{formatDate(r.date)}</td>
                        <td className="px-4 py-2">{r.party}</td>
                        <td className="px-4 py-2">{r.type ?? '—'}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.rate == null ? '—' : `%${r.rate}`}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{formatCurrency(r.base, r.currency)}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium">{formatCurrency(r.withheld, r.currency)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
